import React, {useCallback} from 'react';
import {GatewayGetSchema} from '../../../../../api/DBSchema/Gateway';
import {SMCCheckbox} from '../../../../../components/Simple/SMCCheckbox';
import {useTranslation} from 'react-i18next';
import PlanEditorInnerContainer from '../../../../../components/Editor/PlanEditorInnerContainer';
import Separator from '../../../../../components/Simple/Sepperator';
import Indentation from '../../../../../components/Simple/Indentation';

import {SMCRadio, SMCRadioGroup} from '../../../../../components/Simple/SMCRadio';
import SMCLabel from '../../../../../components/Simple/SMCLabel';
import {SMCSelect, SMCOption} from '../../../../../components/Simple/SMCSelect';
import EmptyRow from '../../../../../components/Simple/EmptyRow';
import EditorLayout from '../../../../../components/Layouts/EditorLayout';
import RowLayout from '../../../../../components/Layouts/RowLayout';
import SMCNumberInput from '../../../../../components/Simple/SMCNumberInput';
import {SMCToggleSwitch} from "../../../../../components/Simple/SMCToggleSwitch";

const pageSchema = GatewayGetSchema.pages.qos;
const pageId = pageSchema.pageId;

function BaseQos({
    pageData,
    setObjectData,
    pageDisabled
}) {
    const { t } = useTranslation();

    const manageInSmp         = pageData[pageSchema.manageInSmp];
    const mode                = pageData[pageSchema.mode];
    const lowLatency          = pageData[pageSchema.lowLatency];
    const guarantee           = pageData[pageSchema.guarantee];
    const guaranteeTraffic    = pageData[pageSchema.guaranteeTraffic];
    const guaranteePercentage = pageData[pageSchema.guaranteePercentage];
    const limitApps           = pageData[pageSchema.limitApps];
    const limitAppsUpload     = pageData[pageSchema.limitAppsUpload];
    const limitAppsDownload   = pageData[pageSchema.limitAppsDownload];
    const limitUploadSize     = pageData[pageSchema.limitUploadSize];
    const limitDownloadSize   = pageData[pageSchema.limitDownloadSize];

    const guaranteeTrafficValues = pageSchema.guaranteeTrafficValues;

    const setPageValue = useCallback((param, newValue) => {
        setObjectData([pageId, param], newValue);
    }, [setObjectData]);


    return (
        <EditorLayout>
            <SMCToggleSwitch
                disabled={pageDisabled}
                checked={manageInSmp}
                onChange={checked => setPageValue(pageSchema.manageInSmp, checked)}
                label={t('SMP.PLAN.MANAGE_IN_SMP')}
            />
            <Separator isMainSeparator/>
            <PlanEditorInnerContainer title={t('SMP.PLANS.INNER_MENU.QOS')} managedInSmp={manageInSmp}>
                <RowLayout>
                    <SMCRadioGroup value={mode ? 'ON' : 'OFF'} onChange={e => setPageValue(pageSchema.mode, e.target.value === 'ON')} disabled={pageDisabled || !manageInSmp}>
                        <SMCRadio value="ON" label={t('SMP.ON')} />
                        <SMCRadio value="OFF" label={t('SMP.OFF')} />
                    </SMCRadioGroup>
                </RowLayout>
                                
                <EmptyRow />

                <SMCLabel bold>{t('SMP.PLAN.SEC_SOFT_BLADES.POLICY')}:</SMCLabel>
                {/* Is low latency */}
                <SMCCheckbox
                    disabled={pageDisabled || !manageInSmp}
                    checked={lowLatency}
                    onChange={e => setPageValue(pageSchema.lowLatency, e.target.checked)}
                    label={t('SMP.PLAN.QOS.LINE1')}
                    tooltip={t('SMP.PLAN.QOS.LINE1.TOOLTIP')}
                />
                {/* Bandwidth guarantee */}
                <RowLayout spacing="4px">
                    <SMCCheckbox
                        inline
                        disabled={pageDisabled || !manageInSmp}
                        checked={guarantee}
                        onChange={e => setPageValue(pageSchema.guarantee, e.target.checked)}
                    />
                    <SMCLabel inline>{t('SMP.PLAN.QOS.GUARANTEE')}</SMCLabel>
                    <SMCNumberInput
                        inline
                        width={50}
                        disabled={pageDisabled || !manageInSmp || !guarantee}
                        value={guaranteePercentage}
                        onChange={e => setPageValue(pageSchema.guaranteePercentage, parseInt(e.target.value))}
                    />
                    <SMCLabel inline>% {t('SMP.PLAN.QOS.LINE2')}</SMCLabel>
                    <SMCSelect
                        inline
                        disabled={pageDisabled || !manageInSmp || !guarantee}
                        value={guaranteeTraffic}
                        onChange={e => setPageValue(pageSchema.guaranteeTraffic, e.target.value)}
                    >
                        <SMCOption value={guaranteeTrafficValues.vpnTraffic}>{t('SMP.PLAN.QOS.VPN_TRAFFIC')}</SMCOption>
                        <SMCOption value={guaranteeTrafficValues.allTraffic}>{t('SMP.PLAN.QOS.ALL_TRAFFIC')}</SMCOption>
                    </SMCSelect>
                </RowLayout>
                {/* Limit bandwidth */}
                <SMCCheckbox
                    disabled={pageDisabled || !manageInSmp}
                    checked={limitApps}
                    onChange={e => setPageValue(pageSchema.limitApps, e.target.checked)}
                    label={t('SMP.PLAN.QOS.LINE3')}
                    tooltip={t('SMP.PLAN.QOS.LINE3.TOOLTIP')}
                />
                <Indentation>
                    {/* Download limit */}
                    <RowLayout>
                        <SMCCheckbox
                            disabled={pageDisabled || !manageInSmp || !limitApps}
                            checked={limitAppsDownload}
                            onChange={e => setPageValue(pageSchema.limitAppsDownload, e.target.checked)}
                        />
                        <SMCLabel inline width={110}>{t('SMP.PLAN.QOS.DOWNLOAD')}:</SMCLabel>
                        <SMCNumberInput
                            disabled={pageDisabled || !manageInSmp || !limitApps || !limitAppsDownload}
                            width={80}
                            value={limitDownloadSize}
                            onChange={e => setPageValue(pageSchema.limitDownloadSize, parseInt(e.target.value))}
                        />
                        <SMCLabel inline>Kbps</SMCLabel>
                    </RowLayout>
                    {/* Upload limit */}
                    <RowLayout>
                        <SMCCheckbox
                            disabled={pageDisabled || !manageInSmp || !limitApps}
                            checked={limitAppsUpload}
                            onChange={e => setPageValue(pageSchema.limitAppsUpload, e.target.checked)}
                        />
                        <SMCLabel inline width={110}>{t('SMP.PLAN.QOS.UPLOAD')}:</SMCLabel>
                        <SMCNumberInput
                            disabled={pageDisabled || !manageInSmp || !limitApps || !limitAppsUpload}
                            width={80}
                            value={limitUploadSize}
                            onChange={e => setPageValue(pageSchema.limitUploadSize, parseInt(e.target.value))}
                        />
                        <SMCLabel inline>Kbps</SMCLabel>
                    </RowLayout>
                </Indentation>
                
                <EmptyRow />

                <SMCLabel><b>{t('SMP.DESCRIPTION')}: </b>{t('SMP.PLAN.QOS.LINE4')}</SMCLabel>
            </PlanEditorInnerContainer>
        </EditorLayout>
    );
}

export default BaseQos;